'use client';

import { useToolUsageReport } from '@/components/tools/use-tool-usage-report';
import { useEffect, useState } from 'react';
import {
  flipCoins,
  generateLoremIpsum,
  generatePassword,
  generateRandomLetters,
  generateRandomNumbers,
  generateRandomString,
  generateRandomWords,
  generateUuids,
  rollDice,
  type GeneratorVariant,
} from '@/lib/browser-generator-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const badges: Record<GeneratorVariant, { badge: string; title: string; action: string }> = {
  'password-generator': {
    badge: 'Password',
    title: 'Generate a strong password',
    action: 'Generate password',
  },
  'uuid-generator': { badge: 'UUID', title: 'Generate UUIDs', action: 'Generate UUIDs' },
  'lorem-ipsum-generator': {
    badge: 'Lorem',
    title: 'Generate lorem ipsum',
    action: 'Generate text',
  },
  'random-number-generator': {
    badge: 'Random',
    title: 'Generate random numbers',
    action: 'Generate numbers',
  },
  'random-string-generator': {
    badge: 'Random',
    title: 'Generate a random string',
    action: 'Generate string',
  },
  'dice-roller': { badge: 'Dice', title: 'Roll dice', action: 'Roll' },
  'coin-flipper': { badge: 'Coin', title: 'Flip a coin', action: 'Flip' },
  'random-letter-generator': {
    badge: 'Letter',
    title: 'Generate random letters',
    action: 'Generate letters',
  },
  'random-word-generator': {
    badge: 'Word',
    title: 'Generate random words',
    action: 'Generate words',
  },
};

export default function BrowserGeneratorToolkit({ variant }: { variant: GeneratorVariant }) {
  const meta = badges[variant];
  const [output, setOutput] = useState('');
  // `variant` is the tool slug, so one hook covers every generator in this toolkit.
  // The toolkit generates once on mount so the page is useful immediately, so `output` alone
  // would count page views. Only a regeneration the visitor asked for counts as use.
  const [userGenerations, setUserGenerations] = useState(0);
  useToolUsageReport(variant, userGenerations > 0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  // Password options
  const [length, setLength] = useState(16);
  const [lowercase, setLowercase] = useState(true);
  const [uppercase, setUppercase] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(true);
  // Shared count
  const [count, setCount] = useState(5);
  // Random number range
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(100);
  // Random string
  const [stringLength, setStringLength] = useState(24);
  const [stringSymbols, setStringSymbols] = useState(false);
  // Dice
  const [diceSides, setDiceSides] = useState(6);

  function generate() {
    setCopyMessage(null);
    try {
      let next = '';
      if (variant === 'password-generator') {
        next = generatePassword({ length, lowercase, uppercase, numbers, symbols });
      } else if (variant === 'uuid-generator') {
        next = generateUuids(count);
      } else if (variant === 'lorem-ipsum-generator') {
        next = generateLoremIpsum(count);
      } else if (variant === 'random-number-generator') {
        next = generateRandomNumbers(min, max, count);
      } else if (variant === 'random-string-generator') {
        next = generateRandomString(stringLength, stringSymbols);
      } else if (variant === 'dice-roller') {
        next = rollDice(count, diceSides);
      } else if (variant === 'coin-flipper') {
        next = flipCoins(count);
      } else if (variant === 'random-word-generator') {
        next = generateRandomWords(count);
      } else {
        next = generateRandomLetters(count);
      }
      setOutput(next);
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not generate output.');
    }
  }

  // Generate once on mount so the page is useful immediately. Deliberately does NOT count as
  // usage: see useToolUsageReport above.
  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleGenerateClick() {
    setUserGenerations((count) => count + 1);
    generate();
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{meta.badge}</span>
        <h2 className="tool-live-title">{meta.title}</h2>
        <p className="tool-live-subtitle">
          Everything is generated locally in your browser using the Web Crypto API. Nothing is
          uploaded or stored.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {variant === 'password-generator' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Length: {length}</span>
                  <input
                    max={64}
                    min={4}
                    onChange={(event) => setLength(Number(event.target.value))}
                    type="range"
                    value={length}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Lowercase (a-z)</span>
                  <input
                    checked={lowercase}
                    onChange={(e) => setLowercase(e.target.checked)}
                    type="checkbox"
                  />
                </label>
                <label className="tool-live-field">
                  <span>Uppercase (A-Z)</span>
                  <input
                    checked={uppercase}
                    onChange={(e) => setUppercase(e.target.checked)}
                    type="checkbox"
                  />
                </label>
                <label className="tool-live-field">
                  <span>Numbers (0-9)</span>
                  <input
                    checked={numbers}
                    onChange={(e) => setNumbers(e.target.checked)}
                    type="checkbox"
                  />
                </label>
                <label className="tool-live-field">
                  <span>Symbols (!@#)</span>
                  <input
                    checked={symbols}
                    onChange={(e) => setSymbols(e.target.checked)}
                    type="checkbox"
                  />
                </label>
              </>
            ) : null}
            {variant === 'uuid-generator' || variant === 'lorem-ipsum-generator' ? (
              <label className="tool-live-field tool-live-field-full">
                <span>{variant === 'uuid-generator' ? 'How many UUIDs' : 'Paragraphs'}</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => setCount(Number(event.target.value))}
                  type="number"
                  value={count}
                />
              </label>
            ) : null}
            {variant === 'random-number-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Minimum</span>
                  <input
                    inputMode="numeric"
                    onChange={(e) => setMin(Number(e.target.value))}
                    type="number"
                    value={min}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Maximum</span>
                  <input
                    inputMode="numeric"
                    onChange={(e) => setMax(Number(e.target.value))}
                    type="number"
                    value={max}
                  />
                </label>
                <label className="tool-live-field">
                  <span>How many</span>
                  <input
                    inputMode="numeric"
                    onChange={(e) => setCount(Number(e.target.value))}
                    type="number"
                    value={count}
                  />
                </label>
              </>
            ) : null}
            {variant === 'random-string-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Length</span>
                  <input
                    inputMode="numeric"
                    onChange={(e) => setStringLength(Number(e.target.value))}
                    type="number"
                    value={stringLength}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Include symbols</span>
                  <input
                    checked={stringSymbols}
                    onChange={(e) => setStringSymbols(e.target.checked)}
                    type="checkbox"
                  />
                </label>
              </>
            ) : null}
            {variant === 'dice-roller' ? (
              <>
                <label className="tool-live-field">
                  <span>How many dice</span>
                  <input
                    inputMode="numeric"
                    onChange={(e) => setCount(Number(e.target.value))}
                    type="number"
                    value={count}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Sides</span>
                  <input
                    inputMode="numeric"
                    onChange={(e) => setDiceSides(Number(e.target.value))}
                    type="number"
                    value={diceSides}
                  />
                </label>
              </>
            ) : null}
            {variant === 'coin-flipper' ||
            variant === 'random-letter-generator' ||
            variant === 'random-word-generator' ? (
              <label className="tool-live-field tool-live-field-full">
                <span>
                  {variant === 'coin-flipper'
                    ? 'How many flips'
                    : variant === 'random-word-generator'
                      ? 'How many words'
                      : 'How many letters'}
                </span>
                <input
                  inputMode="numeric"
                  onChange={(e) => setCount(Number(e.target.value))}
                  type="number"
                  value={count}
                />
              </label>
            ) : null}
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleGenerateClick} type="button">
              {meta.action}
            </button>
            {output ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy result
              </button>
            ) : null}
          </div>

          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Result</span>
            <textarea readOnly rows={8} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally with the Web Crypto API. Refresh or click generate again for a new
            result.
          </p>
        </section>
      </div>
    </div>
  );
}
